// $FlowFixMe
import 'normalize.css';
import './customThemeTippy.css';
import './antd.min.css';
import './Main.css';
import React, { Component, Suspense, useContext, useEffect, useRef } from 'react';
import type { Node } from 'react';

import ConfigProvider from 'antd/lib/config-provider';
import antdEnUs from 'antd/lib/locale-provider/en_US';
import {
  ConfigCatProvider,
  createFlagOverridesFromMap,
  OverrideBehaviour,
  User,
  withConfigCatClient,
  WithConfigCatClientProps,
} from 'configcat-react';
import { useRoutes } from 'hookrouter';
import qs from 'query-string';
import { connect, Provider, useSelector } from 'react-redux';
import { useHistory, BrowserRouter as Router, Redirect, Route, Switch, withRouter } from 'react-router-dom';
import type { Location, Match, RouterHistory } from 'react-router-dom';

import { Actions } from 'actions';
import { AuditPreviewPage } from 'components/pages/AsinAudit/AuditPreviewPage';
import { AuditSharePage } from 'components/pages/AsinAudit/AuditSharePage';
import { trackedEvents } from 'config/trackedEvents.config';
import { identify, page, setActive, track } from 'lib/analytics';

import { computeDefaultReturnTo, useCognitoAuth } from './auth/useCognitoAuth';
import { GlobalPlaceholder } from './components/common/Placeholders';
import MainLayout from './components/layout/MainLayout';
import { CognitoSignIn } from './components/pages/signIn/CognitoSignIn';
import { AxiosContextProvider } from './context/AxiosContext.tsx';
import { UserContextProvider } from './context/UserContext.tsx';
import { getSignedUser } from './lib/auth';
import axios from './lib/axios.factory';
import { FORCED_APP_DOMAIN } from './lib/configcat';
import {
  ensureUserReady,
  HUB_REDIRECT_LOADING_TEXT,
  isReadinessGatedPath,
  isRedirectingResult,
  ReadinessResult,
} from './lib/readiness';
import { newVersion } from './lib/version';
import C6Navbar from './Navbar';
import { signIn, store } from './stores';

type props = {
  logged: boolean,
  user: userT,
  lastVisitedURL: string,
};

type actions = {
  signIn: (user: userT) => void,
};

type allProps = props & actions & WithConfigCatClientProps & { children: Node } & { location: Location };

/**
 * Keeps the authenticated area behind a live Cognito session.
 *
 * The Auth0 version of this decoded the stored JWT and compared `exp` by hand,
 * then kicked off a login when it had passed. oidc-client-ts already tracks
 * expiry and renews in the background, so `isAuthenticated` is the answer to
 * the same question - and unlike a manual `exp` check it accounts for a
 * renewal that is in flight.
 *
 * Children are withheld until a session exists, so nothing below can issue an
 * API call that is certain to 401 while the redirect is being arranged.
 *
 * This guard and `/sign-in` are the only places that start sign-in. Token
 * consumers never navigate. When a refresh fails, `auth/tokenProvider.js`
 * removes the dead session, and this guard picks that up. `/sign-out` never
 * reaches React (see `index.js`), so it needs no exemption here.
 */
const CognitoSessionGuard = ({ children }) => {
  const { isLoading, isAuthenticated, signIn } = useCognitoAuth();
  const startedRef = useRef(false);

  useEffect(() => {
    if (isLoading || isAuthenticated || startedRef.current) {
      return;
    }

    startedRef.current = true;

    signIn({ returnTo: computeDefaultReturnTo() });
  }, [isLoading, isAuthenticated, signIn]);

  if (!isAuthenticated) {
    return <GlobalPlaceholder loadingText="Signing in" />;
  }

  return children;
};

class secureApp extends Component<allProps, { loadingUser: boolean, loadingText: string }> {
  constructor(props: allProps) {
    super(props);
    this.state = { loadingUser: true, loadingText: 'Loading user' };
  }

  componentDidMount() {
    const { signIn, lastVisitedURL } = this.props;

    // Readiness first: `/user` answers 401 for anyone not yet provisioned, so
    // without this a new or not-onboarded user just looks signed out. It is
    // also what sends them to Hub onboarding - see lib/readiness.js.
    const readiness = isReadinessGatedPath()
      ? ensureUserReady({ localStorage, axios })
      : Promise.resolve(ReadinessResult.Ready);

    readiness
      .then((result) => {
        if (result === ReadinessResult.RedirectingToHub) {
          this.setState({ loadingText: HUB_REDIRECT_LOADING_TEXT });
        }

        if (isRedirectingResult(result)) {
          // Leaving the page; stay on the spinner rather than flash sign-in.
          return null;
        }

        return getSignedUser(localStorage, axios).then((user) => {
          this.props.getValue(FORCED_APP_DOMAIN, '', new User(user.id, user.email)).then((v: string) => {
            let url = new URL(window.location.href);

            if (
              lastVisitedURL &&
              lastVisitedURL !== window.location.href &&
              !lastVisitedURL.includes('/sign-out') &&
              window.location.pathname !== '/'
            ) {
              // redirect to last visited URL
              url = new URL(lastVisitedURL);
            }

            // ConfigCat does not allow empty strings, so "null" is used instead
            if (v && v !== 'null') {
              url.hostname = v;
            }

            signIn(user);
            this.setState({ loadingUser: false });
          });
        });
      })
      // no user logged => logging
      .catch((error) => {
        this.setState({ loadingUser: false });
      });
  }

  componentDidUpdate(prevProps, prevState) {
    const { user, impersonated, updateLastVisitedURL } = this.props;
    const { user: prevUser } = prevProps;

    setActive(!impersonated);

    if (user) {
      if ((prevUser && prevUser.id !== user.id) || prevUser === undefined) {
        identify(user.id, {
          email: user.email,
          name: user.fullname,
        });
        track(trackedEvents.login);
      }

      page();
      updateLastVisitedURL(window.location.href);
    }

    newVersion(fetch).then((changed) => {
      if (changed) {
        window.location.reload();
      }
    });
  }

  render() {
    const { loadingUser, loadingText } = this.state;

    if (loadingUser) {
      return <GlobalPlaceholder loadingText={loadingText} />;
    }

    const { logged, user, children, location } = this.props;

    if (logged && user) {
      return (
        <CognitoSessionGuard>
          <C6Navbar />
          {children}
        </CognitoSessionGuard>
      );
    }

    // Get path from url for returnTo
    const returnToPath = encodeURIComponent(`${location.pathname}${location.search}`);

    return (
      <Switch>
        <Route path={['/sign-in']} render={this.connectedSignIn} />
        <Route
          path="/invited/:code"
          render={() => {
            window.location.href = `/v2${window.location.pathname}${window.location.search}${window.location.hash}`;

            return <GlobalPlaceholder />;
          }}
        />
        <Route
          path="/oauth/google/callback"
          render={({ match, history, location }: { match: Match, history: RouterHistory, location: Location }) => {
            if (location && location.search) {
              const { code } = qs.parse(location.search);

              return <Redirect to={`/oauth/google/callback?code=${code}`} />;
            }
          }}
        />
        <Redirect to={`/sign-in?returnTo=${returnToPath}`} />
      </Switch>
    );
  }

  connectedSignIn = () => <CognitoSignIn />;
}

const mapStateToProps = function (state): props {
  const userState = state.user;

  return {
    user: userState.user,
    logged: userState.logged,
    impersonated: userState.impersonated,
    lastVisitedURL: state.front.lastVisitedURL,
  };
};

const dispatchToProps = (dispatch: Dispatch<*>, ownProps: Object) => ({
  updateLastVisitedURL: (args) => {
    dispatch(Actions.front.lastVisitedURL.request(args));
  },
  signIn: (args) => dispatch(signIn(args)),
  signOut: () => {
    window.location.href = '/sign-out';
  },
});

const SecureApp = withConfigCatClient(withRouter(connect(mapStateToProps, dispatchToProps)(secureApp)));

const Routes = () => (
  <Switch>
    <Route path="/audit/share">
      <AuditSharePage />
    </Route>
    <Route path="/audit/preview">
      <AuditPreviewPage />
    </Route>
    <SecureApp>
      <MainLayout />
    </SecureApp>
  </Switch>
);

export default function () {
  const overrides = {};
  // Example code:
  // if (!!localStorage.getItem("myFlag")) {
  //   overrides.MY_FLAG = true;
  // }

  const flagOverrides = createFlagOverridesFromMap(overrides, OverrideBehaviour.LocalOverRemote);

  return (
    <ConfigCatProvider sdkKey={window.env.REACT_APP_CONFIG_CAT_SDK_KEY} options={{ flagOverrides }}>
      <Suspense fallback={<GlobalPlaceholder />}>
        <Router>
          <Provider store={store}>
            <AxiosContextProvider>
              <UserContextProvider publicPagesPattens={['/', '', '/login', '/sign-in']}>
                <ConfigProvider locale={antdEnUs}>
                  <Routes />
                </ConfigProvider>
              </UserContextProvider>
            </AxiosContextProvider>
          </Provider>
        </Router>
      </Suspense>
    </ConfigCatProvider>
  );
}
